import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TrackSelect } from "@/components/admin/Track";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const splitLine = (line) => {
  const out = []; let cur = ""; let q = false;
  for (const ch of line) {
    if (ch === '"') q = !q;
    else if ((ch === "," || ch === ";" || ch === "\t") && !q) { out.push(cur.trim()); cur = ""; }
    else cur += ch;
  }
  out.push(cur.trim());
  return out;
};

export const parseCsv = (text) => {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return [];
  let nameIdx = -1, emailIdx = -1, start = 0;
  const head = splitLine(lines[0]).map((h) => h.toLowerCase());
  if (head.some((h) => h.includes("email"))) {
    emailIdx = head.findIndex((h) => h.includes("email"));
    nameIdx = head.findIndex((h) => h.includes("name"));
    start = 1;
  }
  const rows = [];
  for (const line of lines.slice(start)) {
    const cells = splitLine(line);
    let email, name;
    if (emailIdx >= 0) { email = cells[emailIdx]; name = nameIdx >= 0 ? cells[nameIdx] : ""; }
    else {
      const ei = cells.findIndex((c) => EMAIL_RE.test(c));
      email = ei >= 0 ? cells[ei] : cells[cells.length - 1];
      name = cells.filter((_, i) => i !== ei).join(" ").trim();
    }
    rows.push({ name: name || "", email: (email || "").toLowerCase(), valid: EMAIL_RE.test(email || "") });
  }
  return rows;
};

export const BulkImportDialog = ({ credits, subAdmin, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [label, setLabel] = useState("");
  const [expires, setExpires] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [track, setTrack] = useState("comeback");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const rows = parseCsv(text);
  const valid = rows.filter((r) => r.valid);
  const dupes = valid.length - new Set(valid.map((r) => r.email)).size;
  const need = valid.length - dupes;
  const short = subAdmin && need > (credits ?? 0);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const reset = () => { setText(""); setLabel(""); setExpires(""); setResult(null); setSendEmail(true); if (fileRef.current) fileRef.current.value = ""; };

  const submit = async () => {
    if (!need) { toast.error("Add at least one row with a valid email."); return; }
    if (need > 100) { toast.error("Maximum 100 candidates per import."); return; }
    setBusy(true);
    try {
      const res = await api.post("/admin/codes/bulk", {
        rows: valid.map(({ name, email }) => ({ name, email })), label: label.trim(),
        expires_days: expires ? Number(expires) : null, send_email: sendEmail, base_url: window.location.origin, track,
      });
      setResult(res.data);
      toast.success(`Created ${res.data.created} code${res.data.created === 1 ? "" : "s"}${sendEmail ? `, emailed ${res.data.sent}` : ""}`);
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Import failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-xl font-heading font-bold" data-testid="bulk-import-btn">
          <Upload className="w-4 h-4 mr-1" /> Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold">Bulk invite candidates</DialogTitle>
          <DialogDescription className="font-body">
            Upload or paste a CSV with <span className="font-mono">name, email</span> columns. One access code is generated per row{subAdmin ? " (1 credit each)" : ""} and emailed to each candidate.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="bulk-result">
            <div className="grid grid-cols-3 gap-3">
              {[["Created", result.created, "text-slate-900"], ["Emailed", result.sent, "text-emerald-600"], ["Email failed", result.failed, result.failed ? "text-rose-600" : "text-slate-400"]].map(([k, v, c]) => (
                <div key={k} className="rounded-xl border border-slate-200 p-3 text-center">
                  <p className={`font-heading font-extrabold text-2xl ${c}`} data-testid={`bulk-${k.toLowerCase().replace(' ', '-')}`}>{v}</p>
                  <p className="font-body text-xs text-slate-500">{k}</p>
                </div>
              ))}
            </div>
            {result.errors?.length > 0 && (
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs font-body text-rose-700 space-y-1" data-testid="bulk-errors">
                <p className="font-semibold flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Some emails couldn't be sent — the codes were still created and you can email them from the table.</p>
                {result.errors.map((e, i) => <p key={i} className="truncate">{e}</p>)}
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setOpen(false)} className="rounded-xl font-heading font-bold" data-testid="bulk-close-btn">Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" id="csvfile" data-testid="bulk-file-input" />
              <Button type="button" variant="outline" className="rounded-xl font-heading font-bold" onClick={() => fileRef.current?.click()} data-testid="bulk-choose-file-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Choose CSV file
              </Button>
              <span className="font-body text-xs text-slate-400">or paste below</span>
            </div>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={6}
              placeholder={"name,email\nPriya Sharma,priya@example.com\nArjun Mehta,arjun@example.com"}
              className="rounded-xl font-mono text-xs" data-testid="bulk-csv-textarea" />

            <div className="grid sm:grid-cols-4 gap-3">
              <div>
                <Label className="font-heading font-bold text-slate-800">Assessment</Label>
                <TrackSelect value={track} onChange={setTrack} testid="bulk-track-select" />
              </div>
              <div>
                <Label className="font-heading font-bold text-slate-800">Label (optional)</Label>
                <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Cohort June" className="mt-1.5 rounded-xl" data-testid="bulk-label-input" />
              </div>
              <div>
                <Label className="font-heading font-bold text-slate-800">Expires in days</Label>
                <Input type="number" min="1" value={expires} onChange={(e) => setExpires(e.target.value)} placeholder="never" className="mt-1.5 rounded-xl" data-testid="bulk-expiry-input" />
              </div>
              <label className="flex items-center gap-2 mt-7 font-body text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4 accent-primary" data-testid="bulk-send-email-checkbox" />
                Email codes to candidates
              </label>
            </div>

            {rows.length > 0 && (
              <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="bulk-preview">
                <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200 font-body text-xs text-slate-600">
                  <span><span className="font-heading font-bold text-slate-900" data-testid="bulk-valid-count">{need}</span> valid candidate{need === 1 ? "" : "s"}{dupes ? ` · ${dupes} duplicate${dupes === 1 ? "" : "s"} skipped` : ""}{rows.length - valid.length ? ` · ${rows.length - valid.length} invalid` : ""}</span>
                  {subAdmin && <span className={short ? "text-rose-600 font-semibold" : "text-slate-500"} data-testid="bulk-credit-check">{need} credit{need === 1 ? "" : "s"} needed · {credits ?? 0} available</span>}
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-left font-body text-xs">
                    <tbody className="divide-y divide-slate-100">
                      {rows.slice(0, 200).map((r, i) => (
                        <tr key={i} className={r.valid ? "" : "bg-rose-50/60"}>
                          <td className="px-4 py-1.5 w-6">{r.valid ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <AlertCircle className="w-3.5 h-3.5 text-rose-500" />}</td>
                          <td className="px-2 py-1.5 text-slate-800">{r.name || <span className="text-slate-400">—</span>}</td>
                          <td className="px-2 py-1.5 text-slate-600 font-mono">{r.email}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button onClick={submit} disabled={busy || !need || short} className="rounded-xl font-heading font-bold" data-testid="bulk-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                {short ? "Not enough credits" : `Generate ${need || ""} code${need === 1 ? "" : "s"}${sendEmail ? " & email" : ""}`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
